import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import MetricCard from './components/MetricCard';
import DashboardLoader from './components/DashboardLoader';
import {
  productsAPI, servicesAPI, testimonialsAPI, teamAPI,
  careersAPI, faqsAPI, partnersAPI, statsAPI,
  contactAPI, newsletterAPI, howItWorksAPI, usersAPI,
  navigationAPI, companyValuesAPI, socialLinksAPI, companyAPI,
} from '../../services/api';
import './DashboardOverview.css';

const SERVICE_CATEGORIES = [
  { key: 'company', label: 'Company Info', icon: 'building', path: '/dashboard/company', color: '#6366f1', singleton: true, group: 'company' },
  { key: 'companyValues', label: 'Company Values', icon: 'star', path: '/dashboard/company-values', color: '#f59e0b', group: 'company' },
  { key: 'socialLinks', label: 'Social Links', icon: 'share', path: '/dashboard/social-links', color: '#0ea5e9', group: 'company' },
  { key: 'products', label: 'Products', icon: 'folder', path: '/dashboard/products', color: '#6366f1', group: 'content' },
  { key: 'services', label: 'Services', icon: 'layers', path: '/dashboard/services', color: '#8b5cf6', group: 'content' },
  { key: 'team', label: 'Team Members', icon: 'users', path: '/dashboard/team', color: '#06b6d4', group: 'content' },
  { key: 'testimonials', label: 'Testimonials', icon: 'message', path: '/dashboard/testimonials', color: '#f59e0b', group: 'content' },
  { key: 'careers', label: 'Careers', icon: 'briefcase', path: '/dashboard/careers', color: '#10b981', group: 'content' },
  { key: 'faqs', label: 'FAQs', icon: 'help', path: '/dashboard/faqs', color: '#ec4899', group: 'content' },
  { key: 'stats', label: 'Stats', icon: 'chart', path: '/dashboard/stats', color: '#f97316', group: 'data' },
  { key: 'partners', label: 'Partners', icon: 'handshake', path: '/dashboard/partners', color: '#14b8a6', group: 'data' },
  { key: 'howItWorks', label: 'How It Works', icon: 'zap', path: '/dashboard/how-it-works', color: '#a855f7', group: 'data' },
  { key: 'navigation', label: 'Navigation', icon: 'compass', path: '/dashboard/navigation', color: '#0ea5e9', group: 'config' },
];

const FILTER_GROUPS = [
  { key: 'all', label: 'All' },
  { key: 'company', label: 'Company' },
  { key: 'content', label: 'Content' },
  { key: 'data', label: 'Data' },
  { key: 'config', label: 'Site Config' },
];

const SORT_OPTIONS = [
  { key: 'alpha-asc', label: 'A → Z' },
  { key: 'alpha-desc', label: 'Z → A' },
  { key: 'count-desc', label: 'Most Items' },
  { key: 'count-asc', label: 'Least Items' },
  { key: 'has-content', label: 'Has Content First' },
  { key: 'empty-first', label: 'Empty First' },
];

const ADMIN_TOOLS = [
  { key: 'users', label: 'User Management', description: 'Manage user accounts and roles', icon: 'shield', path: '/dashboard/users' },
  { key: 'messages', label: 'Messages', description: 'View and manage contact messages', icon: 'mail', path: '/dashboard/messages' },
  { key: 'newsletter', label: 'Newsletter', description: 'Manage newsletter subscribers', icon: 'send', path: '/dashboard/newsletter' },
];

const ICONS = {
  folder: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  layers: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  users: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  message: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  briefcase: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  help: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  chart: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  handshake: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>,
  zap: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  shield: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  mail: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  send: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  activity: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  database: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  globe: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  check: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  compass: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  building: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h.01"/><path d="M9 12h.01"/><path d="M9 15h.01"/><path d="M9 18h.01"/><path d="M15 9h.01"/><path d="M15 12h.01"/><path d="M15 15h.01"/><path d="M15 18h.01"/></svg>,
  star: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  share: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
};

export default function DashboardOverview() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('alpha-asc');
  const [filterGroup, setFilterGroup] = useState('all');

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const apis = {
        products: productsAPI.list,
        services: servicesAPI.list,
        team: teamAPI.list,
        testimonials: testimonialsAPI.list,
        careers: careersAPI.list,
        faqs: faqsAPI.list,
        stats: statsAPI.list,
        partners: partnersAPI.list,
        howItWorks: howItWorksAPI.list,
        navigation: navigationAPI.list,
        companyValues: companyValuesAPI.list,
        socialLinks: socialLinksAPI.list,
      };

      // Special counters for non-list resources (e.g. singleton KV stores)
      const specialCounters = {
        // Company is a key-value singleton — count populated simple fields
        company: async () => {
          const res = await companyAPI.get();
          const data = res.data || {};
          return Object.entries(data).reduce((acc, [key, value]) => {
            if (key === 'values' || key === 'social') return acc;
            if (value === null || value === undefined) return acc;
            const str = String(value).trim();
            return str.length > 0 ? acc + 1 : acc;
          }, 0);
        },
      };

      if (isAdmin) {
        apis.users = usersAPI.list;
        apis.messages = contactAPI.list;
        apis.newsletter = newsletterAPI.list;
      }

      const results = await Promise.allSettled(
        [
          ...Object.entries(apis).map(async ([key, fn]) => {
            const res = await fn();
            return [key, Array.isArray(res.data) ? res.data.length : 0];
          }),
          ...Object.entries(specialCounters).map(async ([key, fn]) => {
            const count = await fn();
            return [key, count];
          }),
        ]
      );

      const newCounts = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          const [key, count] = r.value;
          newCounts[key] = count;
        }
      });

      setCounts(newCounts);
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  if (loading) return <DashboardLoader text="Loading dashboard..." />;

  const totalContent = Object.values(counts).reduce((sum, c) => sum + c, 0);

  return (
    <div className="dash-overview">
      {/* Welcome Section */}
      <div className="dash-overview__welcome">
        <div className="dash-overview__welcome-text">
          <h1 className="dash-overview__welcome-title">
            Welcome back, {user?.full_name?.split(' ')[0] || user?.username}
          </h1>
          <p className="dash-overview__welcome-sub">
            Manage your content and monitor your platform from here.
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="dash-overview__metrics">
        <MetricCard
          icon={ICONS.database}
          value={totalContent}
          label="Total Content"
        />
        <MetricCard
          icon={ICONS.folder}
          value={counts.products || 0}
          label="Products"
        />
        <MetricCard
          icon={ICONS.layers}
          value={counts.services || 0}
          label="Services"
        />
        <MetricCard
          icon={ICONS.globe}
          value={counts.team || 0}
          label="Team Members"
        />
      </div>

      {/* Service Categories Grid */}
      <div className="dash-overview__section">
        <div className="dash-overview__section-header">
          <h2 className="dash-overview__section-title">Content Management</h2>
          <p className="dash-overview__section-desc">Manage all your platform content in one place</p>
        </div>

        {/* Toolbar: search, filters, sort */}
        <div className="dash-cm-toolbar">
          <div className="dash-cm-toolbar__search">
            <svg className="dash-cm-toolbar__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="dash-cm-toolbar__search-input"
              type="text"
              placeholder="Search sections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="dash-cm-toolbar__search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          <div className="dash-cm-toolbar__filters">
            {FILTER_GROUPS.map((fg) => (
              <button
                key={fg.key}
                className={`dash-cm-toolbar__filter-pill ${filterGroup === fg.key ? 'dash-cm-toolbar__filter-pill--active' : ''}`}
                onClick={() => setFilterGroup(fg.key)}
              >
                {fg.label}
              </button>
            ))}
          </div>

          <div className="dash-cm-toolbar__sort">
            <svg className="dash-cm-toolbar__sort-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>
            </svg>
            <select
              className="dash-cm-toolbar__sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="dash-overview__services-grid">
          {(() => {
            let filtered = SERVICE_CATEGORIES.slice();

            // Filter by group
            if (filterGroup !== 'all') {
              filtered = filtered.filter((c) => c.group === filterGroup);
            }

            // Search
            if (searchQuery.trim()) {
              const q = searchQuery.toLowerCase().trim();
              filtered = filtered.filter((c) => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q));
            }

            // Sort
            filtered.sort((a, b) => {
              switch (sortBy) {
                case 'alpha-asc':
                  return a.label.localeCompare(b.label);
                case 'alpha-desc':
                  return b.label.localeCompare(a.label);
                case 'count-desc':
                  return (counts[b.key] || 0) - (counts[a.key] || 0);
                case 'count-asc':
                  return (counts[a.key] || 0) - (counts[b.key] || 0);
                case 'has-content':
                  return (counts[b.key] || 0) > 0 && (counts[a.key] || 0) === 0 ? 1
                    : (counts[a.key] || 0) > 0 && (counts[b.key] || 0) === 0 ? -1
                    : a.label.localeCompare(b.label);
                case 'empty-first':
                  return (counts[a.key] || 0) === 0 && (counts[b.key] || 0) > 0 ? -1
                    : (counts[b.key] || 0) === 0 && (counts[a.key] || 0) > 0 ? 1
                    : a.label.localeCompare(b.label);
                default:
                  return 0;
              }
            });

            if (filtered.length === 0) {
              return (
                <div className="dash-cm-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <p>No sections match your filters</p>
                  <button className="btn-action btn-action--outline" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }} onClick={() => { setSearchQuery(''); setFilterGroup('all'); }}>
                    Clear Filters
                  </button>
                </div>
              );
            }

            return filtered.map((cat) => (
              <div
                key={cat.key}
                className="dash-service-card"
                onClick={() => navigate(cat.path)}
              >
                <div className="dash-service-card__header">
                  <div className="dash-service-card__icon" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                    {ICONS[cat.icon]}
                  </div>
                  <span className="dash-service-card__count">{counts[cat.key] || 0}</span>
                </div>
                <h3 className="dash-service-card__title">{cat.label}</h3>
                <div className="dash-service-card__bar">
                  <div
                    className="dash-service-card__bar-fill"
                    style={{
                      width: `${totalContent > 0 ? Math.max(((counts[cat.key] || 0) / totalContent) * 100, 5) : 5}%`,
                      backgroundColor: cat.color,
                    }}
                  />
                </div>
                <div className="dash-service-card__arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Admin Tools */}
      {isAdmin && (
        <div className="dash-overview__section">
          <div className="dash-overview__section-header">
            <h2 className="dash-overview__section-title">Management Tools</h2>
            <p className="dash-overview__section-desc">Administrative tools and utilities</p>
          </div>
          <div className="dash-overview__admin-grid">
            {ADMIN_TOOLS.map((tool) => (
              <div
                key={tool.key}
                className="dash-admin-card"
                onClick={() => navigate(tool.path)}
              >
                <div className="dash-admin-card__icon">
                  {ICONS[tool.icon]}
                </div>
                <div className="dash-admin-card__text">
                  <h3 className="dash-admin-card__title">{tool.label}</h3>
                  <p className="dash-admin-card__desc">{tool.description}</p>
                </div>
                {counts[tool.key] !== undefined && (
                  <span className="dash-admin-card__badge">{counts[tool.key]}</span>
                )}
                <div className="dash-admin-card__arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="dash-overview__section">
        <div className="dash-overview__section-header">
          <h2 className="dash-overview__section-title">Quick Summary</h2>
        </div>
        <div className="dash-overview__quick-stats">
          {SERVICE_CATEGORIES.map((cat) => (
            <div key={cat.key} className="dash-quick-stat">
              <span className="dash-quick-stat__label">{cat.label}</span>
              <span className="dash-quick-stat__value">{counts[cat.key] || 0}</span>
              <div className="dash-quick-stat__bar">
                <div
                  className="dash-quick-stat__bar-fill"
                  style={{
                    width: `${totalContent > 0 ? ((counts[cat.key] || 0) / totalContent) * 100 : 0}%`,
                    backgroundColor: cat.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
